<template>
  <div
    class="board-grid-view"
    @click="boardClickHandler"
    @mousemove="mouseMoveHandler"
    @mousewheel="mouseMoveHandler"
  >
    <div class="board-grid-view-main-wrap">
      <div
        v-if="!isMobile"
        :class="{ hidden: !boardPills.length || embeddedBoardAttributes?.disableFilter }"
        class="subheader"
      >
        <FilterPills :key="currentBoardId" />
        <SaveAsNewViewButton v-if="!inEpicBoard" />
      </div>
      <BoardGridViewContent v-if="!isCardsLayoutTimeline && !isCardsLayoutCalendar" />
      <NoResource
        v-if="noResourceEpic"
        class="epic-board-no-resource"
      >
        <template #header>
          <div class="no-resource-header-wrap flex-col">
            <span>{{
              isEpic ? translate('projectsAreTrackedOnRoadmap') : translate('addCardsToProject')
            }}</span>
          </div>
        </template>
        <template #body>
          <span v-if="isEpic">{{ translate('projectEmptyStateDescription') }}</span>
          <InEpicBoardNoResourceBody
            v-else
            :current-board-id="currentBoardId"
          />
        </template>
        <template
          v-if="!isEpic"
          #footer
        >
          <span class="no-resource-footer-wrap">
            {{ translate('cardInProjectEmptyStateFooter') }}
          </span>
        </template>
      </NoResource>
      <TimelineView
        v-if="isCardsLayoutTimeline"
        class="board-view-wrap"
      />
      <CalendarView v-if="isCardsLayoutCalendar" />
    </div>
    <v-popover
      ref="cardQuickPreviewPopover"
      :on-show="() => !draggingIsOn"
      content-class="card-quick-preview"
      placement="top-start"
      :trigger="'manual'"
      :role="'poper'"
      :offset="[88, 20]"
      content-tag="div"
      :get-reference-client-rect="() => position"
      tag="div"
    >
      <template #content="{ state }">
        <BoardListCard
          v-if="state.isVisible"
          :card-id="cardId"
          :index="0"
        />
      </template>
    </v-popover>
  </div>
</template>

<script>
import draggableMixin from '@/mixins/draggable';
import boardItemListMixin from '@/mixins/boardItemListMixin';
import FilterPills from '@/components/widgets/filters/FilterPills';
import SaveAsNewViewButton from '@/components/widgets/filters/SaveAsNewViewButton';
import BoardListCard from '@/components/widgets/BoardListCard';
import EventBus from '@/utilities/eventBus';
import reportingMixin from '@/mixins/reportingMixin';
import TimelineView from '@/components/widgets/TimelineView';
import BoardGridViewContent from '@/components/widgets/BoardGridViewContent';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import CalendarView from '@/boards/calendar/components/CalendarView';
import { BoardLayout } from '@superthread-com/api';
import NoResource from '@/components/widgets/NoResource';
import InEpicBoardNoResourceBody from '@/components/widgets/InEpicBoardNoResourceBody.vue';
import isActiveElementInput from '@/utilities/isActiveElementInput';

export default {
  name: 'BoardGridView',
  components: {
    FilterPills,
    BoardListCard,
    SaveAsNewViewButton,
    TimelineView,
    BoardGridViewContent,
    CalendarView,
    NoResource,
    InEpicBoardNoResourceBody,
  },
  mixins: [draggableMixin, reportingMixin, boardItemListMixin],

  inject: {
    epicId: {
      from: 'epicId',
      default: '',
    },
  },

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
    };
  },

  data() {
    return {
      cardId: '',
      previewPopoverKey: false,
      listenersAdded: false,
    };
  },

  computed: {
    boardPills() {
      return this.$store.getters.boardsPills(this.currentBoardId);
    },
    board() {
      return this.$store.getters.getFlatBoards[this.currentBoardId] || {};
    },
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes.boardId;
      }
      return this.$store.getters.currentBoardId || '';
    },
    cardsLayout() {
      if (this.embed) {
        return (
          this.embeddedBoardAttributes.layout ||
          this.$store.getters.cardsLayout(this.currentBoardId)
        );
      }
      return this.$store.getters.cardsLayout(this.currentBoardId);
    },
    isCardsLayoutTimeline() {
      return this.board.id && this.cardsLayout === BoardLayout.Timeline;
    },
    isCardsLayoutCalendar() {
      return this.board.id && this.cardsLayout === BoardLayout.Calendar;
    },
    isCardsLayoutList() {
      return this.board.id && this.cardsLayout === BoardLayout.List;
    },
    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },
    showAttachmentPreview() {
      return this.$store.getters.getPreviewAttachmentIndex !== -1;
    },
    mouseSuspended() {
      return this.$store.getters.boardViewMouseSuspended;
    },

    boardHasCards() {
      if (this.inEpicBoard) {
        const epicId = this.currentBoardId.split('-')[1];
        return Object.keys(this.$store.getters.getCardsByEpicId(epicId)).length;
      }
      const boardCards = this.$store.getters.getBoardsCardsIdsByBoardId(this.currentBoardId);
      return boardCards.length;
    },

    isEpic() {
      return this.currentBoardId === this.$constants.epicsBoardId;
    },

    noResourceEpic() {
      return (
        !this.boardHasCards &&
        (this.isEpic || this.inEpicBoard) &&
        !this.isCardsLayoutTimeline &&
        !this.isCardsLayoutCalendar &&
        !this.isCardsLayoutList
      );
    },

    cardPreviewShown() {
      return this.previewPopoverKey && this.cardId;
    },
  },

  watch: {
    currentBoardId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getLastNDays(7);
        this.fetchReportData();
      }
    },
    cardPreviewShown(newVal) {
      this.$refs.cardQuickPreviewPopover?.[newVal ? 'show' : 'hide']?.();
    },
  },

  activated() {
    this.addPopoverKeyEvents();
  },

  mounted() {
    this.addPopoverKeyEvents();
    EventBus.$on('child-quick-preview', this.setCardId);
    this.getLastNDays(7);
    this.fetchReportData();
  },

  beforeUnmount() {
    this.closeAddCardIfOpen();
    this.removePopoverKeyEvents();
    EventBus.$off('child-quick-preview', this.setCardId);
  },

  deactivated() {
    this.removePopoverKeyEvents();
  },

  methods: {
    closeAddCardIfOpen() {
      if (this.$store.getters.getListDraftCardCreateView) {
        this.$store.dispatch('setListDraftCardCreateView', '');
      }
    },

    addPopoverKeyEvents() {
      if (this.listenersAdded) return;
      window.addEventListener('keydown', this.popoverKeyOn, true);
      window.addEventListener('keyup', this.popoverKeyOff, true);
      this.listenersAdded = true;
    },

    removePopoverKeyEvents() {
      if (!this.listenersAdded) return;
      window.removeEventListener('keydown', this.popoverKeyOn, true);
      window.removeEventListener('keyup', this.popoverKeyOff, true);
      this.listenersAdded = false;
    },

    popoverKeyOn(e) {
      if (isActiveElementInput()) {
        return;
      }
      if (
        (this.$route.name !== 'board' && this.$route.name !== 'sprint') ||
        this.isAnyCardDisplayed ||
        this.showAttachmentPreview
      )
        return;
      if (e && (e.shiftKey || e.code === 'Space')) {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) {
          return;
        }
        this.previewPopoverKey = true;
      }
    },

    popoverKeyOff() {
      this.previewPopoverKey = false;
    },

    setCardId(cardId, position) {
      this.cardId = cardId;

      if (!position || (position instanceof DOMRect ? false : Object.keys(position).length === 0))
        return;
      this.setPosition(position);
    },

    setPosition(position) {
      this.position = position;
    },

    mouseMoveHandler() {
      if (this.mouseSuspended) {
        this.$store.dispatch('setBoardViewMouseSuspended', false);
      }
    },

    boardClickHandler() {
      if (!this.mouseSuspended) {
        // remove card highlight on board click
        this.$store.dispatch('setSelectedCardId', '');
      }
    },
  },
};
</script>

<style lang="scss">
$pillsContainerPadding: 24px;

.board-grid-view {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: calc(100vh - (#{$headerHeight}));

  &.in-epic-board {
    height: calc(100vh - 2 * (#{$headerHeight}));

    .board-grid-view-main-wrap {
      height: calc(100vh - 2 * (#{$headerHeight}));
    }
  }

  .board-grid-view-main-wrap {
    padding: 0px;
    height: calc(100vh - (#{$headerHeight}));
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    @apply bg-background-strongest;

    .board-view-wrap {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      min-width: 0;

      .__panel {
        outline: none;
      }

      &.__vuescroll {
        .__rail-is-horizontal {
          z-index: 3 !important;
        }

        .__view {
          display: flex;
          min-width: 0 !important;
          flex-grow: 1;
          max-height: 100%;
        }
        &.rail-index-20 {
          .__rail-is-vertical {
            @apply z-20 !important;
          }
        }
      }

      .board-column {
        display: flex;

        flex-shrink: 0;
        flex-grow: 0;
        max-height: 100%;

        .empty-group {
          flex-basis: 0 !important;
        }

        &.sortable-ghost {
          .draggable-cards {
            border-radius: 12px;
            height: auto !important;
            min-height: unset;
            @apply bg-hover-default;
          }

          .__vuescroll {
            .__rail-is-vertical {
              display: none !important;
            }
          }

          .draft-list-create-wrap {
            visibility: hidden;
          }

          .list-header,
          .list-row,
          .list-footer {
            visibility: hidden;
          }
        }

        &.sortable-drag {
          border-left: 1px solid transparent;
          border-right: 1px solid transparent;

          .draggable-cards {
            border-radius: 12px;
            height: auto !important;
            backdrop-filter: blur(4px);
            min-height: unset;
            .list-header,
            .list-footer {
              background: transparent;
            }
            @apply bg-draggElement-background;
          }
          .__vuescroll {
            .__rail-is-vertical {
              display: none !important;
            }
          }

          .list-content {
            overflow-y: hidden;
          }
          .list-footer {
            position: relative;
          }

          .drag-list-rows-wrap {
            display: flex;
            flex-direction: column;
            order: 2;
            overflow: hidden;
          }
        }
      }

      .draggable-content {
        display: flex;
        flex-grow: 1;
        padding: 0 $draggableContentHPadding;
        min-width: 0;
        align-items: stretch;
        margin-top: 8px;
      }

      &.scrolling {
        .board-column {
          .board-list-card-wrap {
            pointer-events: none;
          }
        }
      }
    }

    .subheader {
      @apply flex
      items-center
      px-6
      py-[6px]
      relative
      bg-selected-bg
      z-[13]
      border-0 border-b border-solid border-dividers-default;
    }
  }
  .epic-board-no-resource {
    @apply absolute mt-56 left-1/2 text-center text-sm pointer-events-none;
    transform: translate(-50%, 0);
    max-width: 420px;
  }
}

.tippy-box {
  .tippy-content {
    .card-quick-preview {
      width: $columnWidth;
    }
  }
}

.mobile {
  .board-grid-view {
    height: calc(var(--mvh) - (#{$headerHeight}));

    .board-grid-view-main-wrap {
      height: calc(var(--mvh) - (#{$headerHeight}));
    }
  }
}

@import '@/assets/scss/modules/cards/_cards-layout--list.scss';
</style>
